import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React from 'react';

import HelpTooltip from 'help/HelpTooltip';
import getTestProps from 'test/lib/cypress/getTestProps';
import {Title} from 'ui-library/Headers';
import Link from 'ui-library/Link';

import {RegressionParameterConstraintType} from '../RegressionTypes';
import RegressionConfigInitialValuesEditor from './RegressionConfigInitialValuesEditor';
import {RegressionConfigModalState} from './RegressionConfigModalTypes';
import RegressionParameterConstraintEditor from './RegressionParameterConstraintEditor';

const INITIAL_PARAMETER_OVERRIDE_HELP_TEXT =
  'Poor initial values may lead to a suboptimal solution, so we recommend letting Benchling automatically suggest initial parameter values based on your data.';

const BASE_TEST_PROPS = {component: 'RegressionConfigModalParameterSection'};
export interface RegressionConfigModalParameterSectionProps {
  regressionConfigState: RegressionConfigModalState;
  onChangeRegressionConfigState: (newState: RegressionConfigModalState) => void;
}

export default function RegressionConfigModalParameterSection(
  props: RegressionConfigModalParameterSectionProps
): JSX.Element {
  return (
    <div {...getTestProps({...BASE_TEST_PROPS, element: 'root'})}>
      <Title>Constrain parameter values</Title>
      {props.regressionConfigState.parameterCustomizations.map(({name, constraint}, parameterIndex) => (
        <div
          className="u-input-spacing-top"
          key={name}
          {...getTestProps({...BASE_TEST_PROPS, element: 'editor', key: name})}
        >
          <RegressionParameterConstraintEditor
            parameterName={name}
            constraint={constraint}
            onChange={(newConstraint) => {
              props.onChangeRegressionConfigState(
                produce(props.regressionConfigState, (draftState) => {
                  draftState.parameterCustomizations[parameterIndex].constraint = newConstraint;
                  if (newConstraint.type === RegressionParameterConstraintType.EQUAL) {
                    draftState.parameterCustomizations[parameterIndex].initialValue = {userDefined: false};
                  }
                })
              );
            }}
          />
        </div>
      ))}
      <div className="u-input-spacing-top u-text-align-right">
        <Link
          onClick={() => {
            props.onChangeRegressionConfigState({
              ...props.regressionConfigState,
              showAdvancedSettings: !props.regressionConfigState.showAdvancedSettings,
            });
          }}
        >
          {props.regressionConfigState.showAdvancedSettings
            ? 'Hide advanced settings'
            : 'Show advanced settings'}
        </Link>
      </div>
      {props.regressionConfigState.showAdvancedSettings && (
        <>
          <hr />
          <Title>
            Initial parameter values{' '}
            <HelpTooltip
              className={css(styles.initialParameterValueHelpIcon)}
              text={INITIAL_PARAMETER_OVERRIDE_HELP_TEXT}
            />
          </Title>
          <RegressionConfigInitialValuesEditor
            regressionConfigState={props.regressionConfigState}
            onChangeRegressionConfigState={props.onChangeRegressionConfigState}
          />
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  initialParameterValueHelpIcon: {
    transform: 'translateY(-1px)',
  },
});
